import type { ReactNode } from "react";
import reactNodeToString from "react-node-to-string";

// Preserve only select valid characters
const invalidSelectorCharsRegEx = /[^\w-]+/g;
// Capture leading digits or dashes
const invalidStartCharRegEx = /^(\d|-)/;

const stripInvalidSelectorChars = (str: string): string =>
  str.replace(invalidSelectorCharsRegEx, "");

/**
 * Make a valid slug from content by stripping invalid characters and joining with dashes
 * @param content - string
 * @returns a slugified string
 */
export const slugify = (content: string): string =>
  content
    .split(" ")
    .map((part, i) => {
      let strippedPart = stripInvalidSelectorChars(part).toLowerCase();
      if (i === 0) {
        // Prefix invalid initial character that is valid elsewhere
        strippedPart = strippedPart.replace(invalidStartCharRegEx, "_$&");
      }
      return strippedPart;
    })
    .join("-");

/**
 *
 * @param content - Any `ReactNode`
 * @returns a slugified string
 * @see `slugify`
 */
export const slugifyReactNode = (content: ReactNode): string => {
  const nodeAsString = reactNodeToString(content);
  return slugify(nodeAsString);
};

/**
 * Pass in an initial string and a prefix and the helper will return the two
 * concatenated, with a delimiter of an underscore or dash
 * If the initial string already had the prefix, it won't be duplicated
 * If the initial string already had the default delimiter, it will
 * be retained and not duplicated
 * Otherwise, a default (underscore) or passed-in delimiter will be used, in that order
 */
export const getPrefixedString = ({
  delimiter = "_",
  ignoreRegex,
  initialString,
  prefix,
}: {
  delimiter?: "_" | "-";
  ignoreRegex?: RegExp;
  initialString: string;
  prefix: string;
}) => {
  const prefixWithDelimiter = prefix.endsWith(delimiter)
    ? prefix
    : `${prefix}${delimiter}`;
  if (
    ignoreRegex?.test(initialString) ||
    initialString.startsWith(prefixWithDelimiter)
  ) {
    return initialString;
  }
  const prefixWithoutDelimiter = prefix.replace(/[_|-]$/, "");
  const returnString = initialString
    .replace(new RegExp(`^${prefixWithoutDelimiter}`, "i"), "")
    .replace(/^[_|-]/, "");
  return `${prefixWithoutDelimiter}${delimiter}${returnString}`;
};
